/*
  AnchorLink:
  Necessary because of the _very annoying_ pattern in Gatsby that
  requires the switch between an `<a/>` and their `<Link/>` component
  depending on whether a link is internal or external. This component
  works as a middle layer that will do all of the necessary logic
  behind the scenes, and will even allow subdomain switching if needed.
  */
import { graphql, Link, useStaticQuery } from "gatsby"
import React, { FC, ReactNode } from "react"

import { AnchorLinkQueryQuery } from "@graphqlTypes"

import { isOnBlogSubdomain, processShortcodes } from "@utils"

const AnchorLink: FC<{
  href?: string | null,
  dontRenderLink?: boolean,
  children: ReactNode,
}> = ({
  children,
  dontRenderLink,
  href,
  ...props
}) => {
    const {
      blogSubdomainPermalinks,
      site: {
        siteMetadata: {
          articlesSiteUrl,
          marketingSiteUrl,
        },
      },
    } = useStaticQuery<AnchorLinkQueryQuery>(graphql`
      query AnchorLinkQuery {
        blogSubdomainPermalinks
        site {
          siteMetadata {
            articlesSiteUrl
            marketingSiteUrl
          }
        }
      }
    `)

    if (dontRenderLink || !href) return children

    const _href = processShortcodes(href)

    if (
      _href === "/pricing/" ||
      _href.includes("//") ||
      _href.includes(".pdf") ||
      _href.includes("#")
    ) {
      return (
        <a href={_href} {...props}>{children}</a>
      )
    }

    // If we're on articles.helpscout.com,
    // append https://www.helpscout.com/ to pages that are _not_ on the subdomain
    if (isOnBlogSubdomain() && !blogSubdomainPermalinks?.includes(_href)) {
      return <a href={`${marketingSiteUrl}${_href}`} {...props}>{children}</a>
    }

    // If we're on www.helpscout.com,
    // append https://articles.helpscout.com/ to pages that are on the subdomain
    if (!isOnBlogSubdomain() && blogSubdomainPermalinks?.includes(_href)) {
      return <a href={`${articlesSiteUrl}${_href}`} {...props}>{children}</a>
    }

    return (
      <Link to={_href} {...props}>{children}</Link>
    )
  }

export default AnchorLink
